import Big from 'big.js';
import { useMemo } from 'react';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerOperationArchiveButton } from '@/modules/issuer-dashboard/components/IssuerOperationArchiveButton.tsx';
import { useTransactionArchiveByIdMutation } from '@/modules/issuer-dashboard/queries/transactions.ts';
import { DisclosureListItem } from 'ui';
import { fixNameEnum, goodFormatNumber, goodFormatUSDCurrency } from 'ui/utils';
import { TransactionPaginateResponse, TransactionType } from 'api';

interface IssuerHolderTransactionTabItemProps {
  transaction: TransactionPaginateResponse;
  isDeleted?: boolean;
  showArchiveButton?: boolean;
}

const IssuerHolderTransactionTabItem = ({
  transaction,
  isDeleted = false,
  showArchiveButton = false,
}: IssuerHolderTransactionTabItemProps) => {
  const {
    id,
    type,
    memo,
    payment_id,
    issue_date,
    register_class,
    price_per_share,
    requested_at_date,
    quantity,
    dividend_event_id,
    register_id,
  } = transaction;

  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { mutateAsync: archiveTransaction } = useTransactionArchiveByIdMutation();

  const bottomContentData = useMemo<Array<[JSX.Element | string, JSX.Element | string]>>(() => {
    const info: Array<[JSX.Element | string, JSX.Element | string]> = [];

    if (requested_at_date) info.push(['Request date', dateToPrint(requested_at_date) || '-']);
    if (issue_date) info.push(['Completion date', dateToPrint(issue_date) || '-']);
    if (price_per_share)
      info.push(['Price per share/unit', goodFormatUSDCurrency(price_per_share).toString()]);

    if (register_id) info.push(['Register ID', `#${register_id}`]);

    if (payment_id) info.push(['Payment ID', `#${payment_id}`]);
    if (id) info.push(['Transaction ID', `#${id}`]);
    if (dividend_event_id) {
      info.push([
        `${getRegisterTypeStrings('dividend', { capitalize: true })} event ID`,
        `#${dividend_event_id}`,
      ]);
    }
    if (memo) info.push(['Memo', memo]);
    if (!isDeleted && showArchiveButton && id) {
      info.push([
        <IssuerOperationArchiveButton
          id={id}
          entityName="transaction"
          onArchive={archiveTransaction}
        />,
        <></>,
      ]);
    }

    return info;
  }, [
    requested_at_date,
    issue_date,
    price_per_share,
    register_id,
    payment_id,
    id,
    dividend_event_id,
    memo,
    getRegisterTypeStrings,
    isDeleted,
    showArchiveButton,
    archiveTransaction,
  ]);

  const name = useMemo(() => {
    if (type === TransactionType.Subscription) return getRegisterTypeStrings('application');
    if (type === TransactionType.Drp) return 'Dividend reinvestment';
    return fixNameEnum(type);
  }, [getRegisterTypeStrings, type]);
  return (
    <DisclosureListItem
      leftMainString={name}
      rightMainString={goodFormatUSDCurrency(
        +new Big(quantity ?? 0)?.mul(price_per_share ?? 0)?.valueOf(),
      )}
      leftSubduedContentSet={[
        issue_date ? dateToPrint(issue_date) : 'No completion date',
        register_class?.class_name || '-',
      ]}
      rightSubduedString={`${goodFormatNumber(quantity ?? 0)} units/shares`}
      classes={{
        rightMainString: 'max-w-[200px] min-w-[140px]',
      }}
      bottomContent={bottomContentData}
    />
  );
};

export default IssuerHolderTransactionTabItem;
